/**
 * ThemeInjector — applies the CMS-managed `customer_theme` / `staff_theme`
 * globally by writing HSL CSS custom properties into `<html>` on route change.
 *
 * Zero-FOUC hydration: reads from localStorage synchronously on mount so the
 * CSS variables are correct on the FIRST paint. Then fires the network query
 * to pick up any subsequent CMS edits (with staleTime: 5min).
 *
 * Design
 *   - `/portal/*`           → customer_theme (accent, card, panel bg, ring)
 *   - `/app/*` or `/driver/*` → staff_theme (accent primary + secondary)
 *   - Everywhere else       → customer_theme (public landing/legal/support pages
 *                              are branded with the customer palette).
 */
import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { CMS_CACHE_KEY } from "@/hooks/useLandingCMS";

// Convert `#RRGGBB` → `H S% L%` triplet (shadcn HSL notation).
function hexToHslTriplet(hex) {
  if (!hex || typeof hex !== "string") return null;
  const clean = hex.replace("#", "").padEnd(6, "0");
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let H;
  let S;
  const L = (max + min) / 2;
  if (max === min) {
    H = 0;
    S = 0;
  } else {
    const d = max - min;
    S = L > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) H = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) H = (b - r) / d + 2;
    else H = (r - g) / d + 4;
    H /= 6;
  }
  return `${Math.round(H * 360)} ${Math.round(S * 100)}% ${Math.round(L * 100)}%`;
}

const OWNED_VARS = [
  "--primary",
  "--primary-foreground",
  "--accent",
  "--accent-foreground",
  "--ring",
  "--background",
  "--card",
];

function apply(mapping) {
  const root = document.documentElement;
  Object.entries(mapping).forEach(([k, v]) => {
    if (v) root.style.setProperty(k, v);
    else root.style.removeProperty(k);
  });
}

function clearOwnedVars() {
  const root = document.documentElement;
  OWNED_VARS.forEach((k) => root.style.removeProperty(k));
}

export default function ThemeInjector() {
  const { pathname } = useLocation();

  // Synchronously hydrate the theme cache on first render (before paint) to
  // eliminate the flash-of-default-theme. If no cache is present we still fall
  // through to Tailwind defaults, which is acceptable for a first-ever visit.
  const cached = (() => {
    try { return JSON.parse(localStorage.getItem(CMS_CACHE_KEY) || "null") || {}; }
    catch { return {}; }
  })();

  const { data } = useQuery({
    queryKey: ["cms-landing"],
    queryFn: () => api.get("/cms/landing").then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 0,
    // NOTE: no `initialData` here on purpose. This is the same query key used
    // by Landing.jsx, which registers its own initialData reading the FULL
    // CMS document from localStorage. If we set an initialData here with only
    // theme+brand, React Query would take our thin object as the source of
    // truth for the whole page (first-mount race) and Landing would render
    // with no hero/features/etc. FOUC is already handled by the synchronous
    // hydrator script in public/index.html.
  });

  // React Query v5 removed useQuery.onSuccess — persist the cache via effect so
  // the synchronous FOUC hydrator in index.html has data on the next paint.
  useEffect(() => {
    const content = data?.content || data;
    if (!content) return;
    try {
      localStorage.setItem(CMS_CACHE_KEY, JSON.stringify({
        customer_theme: content?.customer_theme || null,
        staff_theme: content?.staff_theme || null,
        brand: content?.brand || null,
        _cached_at: Date.now(),
      }));
    } catch { /* private mode / quota */ }
  }, [data]);

  // Compute which theme applies for the current route so we can apply it in a
  // *layout* effect — CSS vars are set BEFORE the browser paints.
  const content = data?.content || data || {};
  const isCustomer = pathname.startsWith("/portal");
  const isStaffShell = pathname.startsWith("/app") || pathname.startsWith("/driver");
  const activeTheme = isStaffShell ? (content.staff_theme || cached.staff_theme) :
                                     (content.customer_theme || cached.customer_theme);
  const isStaff = isStaffShell;

  useLayoutEffect(() => {
    // Favicon — CMS-managed. Upload takes priority over URL.
    const iconHref = content.brand?.favicon || content.seo?.favicon_url;
    if (iconHref) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      if (link.href !== iconHref) link.href = iconHref;
    }

    if (activeTheme?.accent) {
      apply({
        "--primary": hexToHslTriplet(activeTheme.accent),
        "--primary-foreground": "0 0% 100%",
        "--accent": hexToHslTriplet(isStaff ? (activeTheme.accent_secondary || activeTheme.accent) : activeTheme.accent),
        "--accent-foreground": "0 0% 100%",
        "--ring": hexToHslTriplet(activeTheme.accent),
      });
    } else {
      clearOwnedVars();
    }
  }, [activeTheme, isStaff, content.brand?.favicon, content.seo?.favicon_url]);

  // Legacy useEffect kept as a no-op to preserve React Fast Refresh compatibility
  useEffect(() => {}, []);

  return null;
}
